import { Info } from 'lucide-react';

interface CurrencyBasisNoteProps {
  /** The currency the money on screen is in (the response's `currency`). */
  valuesCurrency?: string | null;
  /** The currency that was asked for when no rate into it exists. */
  conversionUnavailable?: string | null;
  /** Reporting currencies left out of the totals on a mixed scope (no rate). */
  excludedCurrencies?: string[] | null;
}

/**
 * CR-7714 BL-14: says when the dashboard's money is not in the currency the
 * user asked for, or leaves some customers out, because an exchange rate is
 * missing. The figures used to go out unconverted under the requested
 * currency's symbol with nothing to say so.
 *
 * @returns The note, or null when every figure is in the requested currency
 */
export function CurrencyBasisNote({ valuesCurrency, conversionUnavailable, excludedCurrencies }: CurrencyBasisNoteProps) {
  const excluded = excludedCurrencies ?? [];
  if (!conversionUnavailable && excluded.length === 0) return null;

  return (
    <div
      role="status"
      data-testid="currency-basis-note"
      className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100"
    >
      <Info className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
      <div>
        {conversionUnavailable && valuesCurrency && (
          <p>
            No exchange rate from {valuesCurrency} to {conversionUnavailable} is on file, so money on this page is shown in {valuesCurrency}.
          </p>
        )}
        {excluded.length > 0 && (
          <p>
            Money from customers reporting in {excluded.join(', ')} is left out of these totals: no exchange rate into {valuesCurrency ?? 'the display currency'} is on file.
          </p>
        )}
      </div>
    </div>
  );
}
